import React from "react";
import { useNavigate } from "react-router-dom";
import '../../src/layouts/styles/about.css';
import SeoHead from '../shared/seo/SeoHead';
import { buildLocalBusinessSchema, buildBreadcrumbSchema } from '../shared/seo/schemas';
import { SITE_BASE } from '../shared/seo/siteUrl';

const STATS = [
    { value: "2017", label: "Established" },
    { value: "6+", label: "Product Lines" },
    { value: "500+", label: "Happy Clients" },
    { value: "Pan India", label: "Delivery" },
];

const VALUES = [
    {
        title: "Craftsmanship",
        desc: "Every product is made with precision by skilled artisans trained in traditional Indian craft techniques passed down through generations.",
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
        ),
    },
    {
        title: "Quality Assured",
        desc: "We source premium raw materials and enforce strict quality checks at every stage — from weaving to finishing — before products leave our facility.",
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
        ),
    },
    {
        title: "Custom Orders",
        desc: "From colour matching to bespoke sizing, we work closely with designers, retailers, and exporters to bring their exact vision to life.",
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
            </svg>
        ),
    },
    {
        title: "Timely Delivery",
        desc: "We understand production deadlines. Our streamlined operations ensure bulk orders are dispatched on time, every time, across India.",
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
        ),
    },
    {
        title: "Competitive Pricing",
        desc: "Direct-from-manufacturer pricing with no middlemen. We offer the best bulk rates without ever compromising on the quality you deserve.",
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
        ),
    },
    {
        title: "Made in India",
        desc: "Proudly rooted in New Delhi, we celebrate Indian artistry and support local craftspeople while bringing handmade beauty to markets worldwide.",
        icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402C1 3.209 4.068 2 6.281 2c1.312 0 4.151.501 5.719 4.457C13.59 2.501 16.464 2 17.726 2 20.267 2 23 3.621 23 7.191c0 4.069-5.136 8.625-11 14.402z"/>
            </svg>
        ),
    },
];

const PRODUCTS = [
    { name: "Pom Poms", desc: "Vibrant, fluffy decorative balls in all sizes and colours for fashion and home décor.", path: "/products/pom-poms" },
    { name: "Designer Tassels", desc: "Elegant hanging ornaments crafted from fine threads, perfect for bags, garments, and interiors.", path: "/products/tassels" },
    { name: "Designer Fringes", desc: "Decorative edge trims that add texture and movement to any fabric or surface.", path: "/products/fringes" },
    { name: "Beaded Patches", desc: "Intricate hand-beaded appliqués for couture garments, bridal wear, and accessories.", path: "/products/beaded-patches" },
];

const About = () => {
    const navigate = useNavigate();

    return (
        <main className="ab-page">
            <SeoHead
                title="About Arte Craft India | Handcrafted Accessories Manufacturer Since 2017"
                description="Arte Craft India — Delhi-based B2B manufacturer of pom poms, tassels, fringes, faux fur and beaded patches since 2017. 500+ clients, ISO quality, OEM manufacturing, ships across India."
                keywords="about Arte Craft India, pom pom manufacturer Delhi, tassel manufacturer India, handcraft accessories manufacturer, OEM craft supplier Delhi, Made in India craft accessories"
                canonical={`${SITE_BASE}/about`}
                author="Arte Craft India"
                robots="index, follow"
                ogType="website"
                ogUrl={`${SITE_BASE}/about`}
                ogSiteName="Arte Craft India"
                ogImage={`${SITE_BASE}/images/homepage-og.jpg`}
                ogImageAlt="Arte Craft India — handcrafted pom poms, tassels and fringes manufacturer in Delhi since 2017"
                ogLocale="en_IN"
                twitterCard="summary_large_image"
                twitterSite="@ArteCraftIndia"
                twitterImage={`${SITE_BASE}/images/homepage-twitter.jpg`}
                geoRegion="IN-DL"
                geoPlacename="Badarpur, New Delhi, India"
                geoPosition="28.5045;77.2855"
                jsonLd={[
                    buildLocalBusinessSchema(),
                    buildBreadcrumbSchema([
                        { name: "Home", url: `${SITE_BASE}/` },
                        { name: "About", url: `${SITE_BASE}/about` },
                    ]),
                ]}
            />

            {/* ── Hero ── */}
            <section className="ab-hero">
                <div className="ab-hero-bg" aria-hidden="true" />
                <div className="ab-hero-inner">
                    <span className="ab-hero-tag">About Us</span>
                    <h1 className="ab-hero-title">
                        Handcrafted with <span className="ab-hero-accent">Tradition.</span><br />
                        Delivered with <span className="ab-hero-accent">Precision.</span>
                    </h1>
                    <p className="ab-hero-sub">
                        Since 2017, Arte Craft India has been a trusted name in handmade craft accessories —
                        blending traditional Indian artistry with modern design sensibilities.
                    </p>
                    <div className="ab-hero-btns">
                        <button className="ab-btn-primary" onClick={() => navigate('/request-a-quote')}>
                            Request a Quote
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                        </button>
                        <button className="ab-btn-outline" onClick={() => navigate('/contact')}>
                            Contact Us
                        </button>
                    </div>
                </div>
                {/* Decorative craft dots */}
                <div className="ab-hero-dots" aria-hidden="true">
                    {Array.from({ length: 24 }).map((_, i) => <span key={i} />)}
                </div>
            </section>

            {/* ── Stats strip ── */}
            <div className="ab-stats">
                {STATS.map(s => (
                    <div key={s.label} className="ab-stat">
                        <div className="ab-stat-value">{s.value}</div>
                        <div className="ab-stat-label">{s.label}</div>
                    </div>
                ))}
            </div>

            {/* ── Our Story ── */}
            <section className="ab-section ab-story-section">
                <div className="ab-container ab-story-grid">

                    {/* Image / visual side */}
                    <div className="ab-story-visual">
                        <div className="ab-story-logo-wrap">
                            <img
                                src={require('../assets/img/logo/artecraftindia.jpg')}
                                alt="Arte Craft India"
                                className="ab-story-logo"
                            />
                            <div className="ab-story-badge">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402C1 3.209 4.068 2 6.281 2c1.312 0 4.151.501 5.719 4.457C13.59 2.501 16.464 2 17.726 2 20.267 2 23 3.621 23 7.191c0 4.069-5.136 8.625-11 14.402z"/>
                                </svg>
                                Made in India
                            </div>
                        </div>

                        <div className="ab-story-card ab-story-card--blue">
                            <div className="ab-story-card-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                </svg>
                            </div>
                            <div className="ab-story-card-text">Quality Assured</div>
                            <div className="ab-story-card-sub">Since 2017</div>
                        </div>

                        <div className="ab-story-card ab-story-card--orange">
                            <div className="ab-story-card-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                            </div>
                            <div className="ab-story-card-text">500+ Clients</div>
                            <div className="ab-story-card-sub">Across India</div>
                        </div>
                    </div>

                    {/* Text side */}
                    <div className="ab-story-text">
                        <span className="ab-section-tag">Our Story</span>
                        <h2 className="ab-section-title">Born from a Passion for Indian Craft</h2>
                        <p>
                            Arte Craft India was founded in 2017 in the heart of New Delhi with a single
                            purpose — to bring the finest handmade craft accessories to designers,
                            boutiques, and manufacturers across India.
                        </p>
                        <p>
                            What began as a small workshop has grown into a full-scale manufacturing unit,
                            employing skilled artisans who carry forward India's rich tradition of handcraft.
                            Our team creates each product with meticulous attention to detail, from the
                            selection of raw materials to the final quality inspection.
                        </p>
                        <p>
                            Today, we supply Pom Poms, Designer Tassels, Fringes, and Beaded Patches
                            to hundreds of clients — from independent designers
                            to large garment exporters — all over India.
                        </p>

                        <div className="ab-story-highlights">
                            <div className="ab-highlight">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                Skilled artisans using traditional techniques
                            </div>
                            <div className="ab-highlight">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                Bulk orders with competitive wholesale pricing
                            </div>
                            <div className="ab-highlight">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                Custom colour, size, and design options available
                            </div>
                            <div className="ab-highlight">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                Pan India shipping with on-time delivery
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Values ── */}
            <section className="ab-section ab-values-section">
                <div className="ab-container">
                    <div className="ab-section-head">
                        <span className="ab-section-tag">Why Choose Us</span>
                        <h2 className="ab-section-title">What Sets Us Apart</h2>
                        <p className="ab-section-sub">
                            We don't just manufacture — we partner with you to ensure every product
                            meets your exact standard.
                        </p>
                    </div>
                    <div className="ab-values-grid">
                        {VALUES.map(v => (
                            <div key={v.title} className="ab-value-card">
                                <div className="ab-value-icon">{v.icon}</div>
                                <h3 className="ab-value-title">{v.title}</h3>
                                <p className="ab-value-desc">{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Products overview ── */}
            <section className="ab-section ab-products-section">
                <div className="ab-container">
                    <div className="ab-section-head">
                        <span className="ab-section-tag">Our Products</span>
                        <h2 className="ab-section-title">What We Manufacture</h2>
                        <p className="ab-section-sub">
                            Six distinct product lines, each crafted to the highest standard for fashion, home décor, and export.
                        </p>
                    </div>
                    <div className="ab-products-grid">
                        {PRODUCTS.map((p, i) => (
                            <div key={p.name} className="ab-product-card" onClick={() => navigate(p.path)}>
                                <div className="ab-product-num">0{i + 1}</div>
                                <h3 className="ab-product-name">{p.name}</h3>
                                <p className="ab-product-desc">{p.desc}</p>
                                <span className="ab-product-link">
                                    View Products
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M12 5l7 7-7 7"/>
                                    </svg>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="ab-cta">
                <div className="ab-cta-bg" aria-hidden="true" />
                <div className="ab-cta-inner">
                    <h2>Ready to Place a Bulk Order?</h2>
                    <p>Share your requirements and we'll get back with a custom quote within 24 hours.</p>
                    <div className="ab-cta-btns">
                        <button className="ab-btn-primary ab-btn-primary--lg" onClick={() => navigate('/request-a-quote')}>
                            Request a Free Quote
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                        </button>
                        <a href="https://wa.me/919818548369" target="_blank" rel="noreferrer" className="ab-cta-wa">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                                <path d="M11.999 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.948-1.42A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/>
                            </svg>
                            WhatsApp Us
                        </a>
                    </div>
                </div>
            </section>

        </main>
    );
};

export default About;
